import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHand, faPen, faHighlighter, faArrowRight,
  faFont, faEraser, faRotateLeft, faTrash, faCamera,
  faPlus, faMinus, faRotate, faCopy, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

// Inline outline-rectangle icon — FA Free 7 dropped faVectorSquare and the
// only square in solid (faSquare) is filled, which reads as a "stop"
// button rather than a "draw rectangle" tool. 14×14 outlined rect matches
// the sizing of the surrounding FontAwesome glyphs.
const RectIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ verticalAlign: 'middle' }}>
    <rect x="2" y="3" width="12" height="10" rx="0.5" />
  </svg>
);

const ROLE_INK = {
  teacher: '#ef4444',
  examiner: '#22c55e',
  administrator: '#22c55e',
  management: '#22c55e'
};

// Zoomable, pannable image viewer with a Bandicam-style annotation layer.
// Tools: hand (pan) / pen / highlighter / rect / arrow / text / eraser.
// Annotations live in component state only — closing the modal drops them;
// the underlying image file is never modified. The "Save screenshot"
// button renders the composite (image + annotations) to a PNG download.
//
// Coordinate model: annotations are stored in CSS pixels of the rendered
// (unzoomed) image. The image+SVG share one CSS transform (pan + scale),
// so drawings stick to the same point on the image when the user zooms.
//
// Drop-in replacement for ZoomableImage where annotation is wanted.
// Optional onSaveForRecheck(blob, fileName): when provided, the green Save
// button hands the annotated PNG to the parent (e.g. queue for a re-check)
// instead of triggering a file download.
// Teachers annotate in red; examiners/admins annotate in green.
export default function AnnotatableImage({
  url,
  alt,
  width = '85vw',
  height = '80vh',
  background = '#0a0a0a',
  onSaveForRecheck = null,
  saveButtonLabel = 'Save',
  // Optional initial viewport. 'top20' zooms so the upper portion of the
  // page fills the canvas (used by Sort tab to show name/roll header).
  initialFocus = null,
  // Absolute zoom multiplier when applying initialFocus (e.g. 6.3 = 630%).
  // When unset, top20 derives zoom from a 20% page-height fraction.
  initialZoom = null,
  // Toolbar defaults. Every viewer opens on the hand so a drag pans.
  // Sort tab uses a reduced tool set and a fixed stroke size.
  defaultTool = 'hand',
  availableTools = null, // null = all tools; else e.g. ['hand','rect','eraser']
  fixedStrokeWidth = null,
  // When set, the pen color is chosen from this list instead of the role ink.
  inkColors = null,
  defaultColor = null,
  defaultStrokeWidth = null,
  maxStroke = 20,
  // Save bakes the drawing into the original pixels. Screenshots stay at 2× PNG.
  exportScale = 2,
  exportMime = 'image/png',
  exportQuality = 0.92,
  requireMarks = false,
  saveTitle = ''
}) {
  const { currentUser } = useAuth();
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 10;
  const inkColor = ROLE_INK[currentUser?.role] || '#ef4444';
  const ALL_TOOLS = ['hand', 'pen', 'highlighter', 'rect', 'arrow', 'text', 'eraser'];
  const tools = availableTools || ALL_TOOLS;
  const showTool = (id) => tools.includes(id);
  const lockedStroke = typeof fixedStrokeWidth === 'number' ? fixedStrokeWidth : null;

  const containerRef = useRef(null);   // outer fixed-size box
  const stageRef     = useRef(null);   // transformed wrapper (image + svg)
  const imageRef     = useRef(null);
  const svgRef       = useRef(null);
  const initialFocusAppliedRef = useRef(false);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan]   = useState({ x: 0, y: 0 });
  const draggingRef     = useRef(false);
  const lastPosRef      = useRef({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [fitSize, setFitSize] = useState({ w: 0, h: 0 });

  // Tool state.
  const [tool, setTool]   = useState(defaultTool);
  const [color, setColor] = useState(defaultColor || inkColor);
  const [strokeWidth, setStrokeWidth] = useState(lockedStroke ?? defaultStrokeWidth ?? 3);
  const [annotations, setAnnotations] = useState([]); // array of shapes
  const [draftShape, setDraftShape]   = useState(null); // in-progress shape
  const [textEntry, setTextEntry]     = useState(null); // {x, y, value}
  const [exporting, setExporting]     = useState(false);
  const [copyState, setCopyState]     = useState('idle'); // 'idle' | 'copying' | 'copied' | 'error'

  const customInk = Array.isArray(inkColors) && inkColors.length > 0;
  // Keep ink locked to the user's role (teacher = red, examiner = green),
  // unless the caller supplied its own colors (for example a white pen).
  useEffect(() => {
    if (customInk) {
      setColor(defaultColor || inkColors[0]);
      return;
    }
    setColor(inkColor);
  }, [inkColor, customInk, defaultColor, inkColors]);

  // Zoom/pan so the top of the fitted page is pinned to the container top.
  // transformOrigin is center; pan.y shifts the stage accordingly.
  // Prefer an explicit zoom multiplier when provided (e.g. Sort = 6.3).
  const focusTopFraction = useCallback((fitH, fraction = 0.2, fixedZoom = null) => {
    const el = containerRef.current;
    if (!el || !fitH || fraction <= 0) return false;
    const containerH = el.clientHeight;
    if (!containerH) return false;
    const computed = containerH / (fraction * fitH);
    const targetZoom = Math.max(
      MIN_ZOOM,
      Math.min(MAX_ZOOM, typeof fixedZoom === 'number' ? fixedZoom : computed)
    );
    const panY = (fitH * targetZoom) / 2 - containerH / 2;
    setZoom(targetZoom);
    setPan({ x: 0, y: panY });
    return true;
  }, []);

  // Reset transform, tool, and annotations whenever a new image loads.
  // Each page starts on the hand so a drag pans until a draw tool is picked.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setTool(defaultTool);
    setAnnotations([]);
    setDraftShape(null);
    setTextEntry(null);
    initialFocusAppliedRef.current = false;
  }, [url, defaultTool]);

  // Apply Sort-style top-of-page focus once fit size is known.
  useEffect(() => {
    if (initialFocus !== 'top20' || initialFocusAppliedRef.current) return;
    if (!fitSize.h) return;
    if (focusTopFraction(fitSize.h, 0.2, initialZoom)) {
      initialFocusAppliedRef.current = true;
    }
  }, [fitSize, initialFocus, initialZoom, focusTopFraction]);

  const colorSwatches = useMemo(
    () => (customInk ? inkColors : [inkColor]),
    [customInk, inkColors, inkColor]
  );

  // Fit the image as large as possible inside the canvas (no tiny letterboxed preview).
  const recomputeFit = useCallback(() => {
    const el = containerRef.current;
    const img = imageRef.current;
    if (!el || !img || !img.naturalWidth || !img.naturalHeight) return;
    const scale = Math.min(
      el.clientWidth / img.naturalWidth,
      el.clientHeight / img.naturalHeight
    );
    setFitSize({
      w: Math.max(1, Math.floor(img.naturalWidth * scale)),
      h: Math.max(1, Math.floor(img.naturalHeight * scale))
    });
  }, []);

  useEffect(() => {
    recomputeFit();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recomputeFit);
      return () => window.removeEventListener('resize', recomputeFit);
    }
    const ro = new ResizeObserver(() => recomputeFit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeFit, url]);

  // Wheel zoom — registered manually so we can preventDefault on the
  // page scroll. Same logic as ZoomableImage.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setZoom(prev => {
        const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev * factor));
        const ratio = next / prev;
        setPan(p => ({
          x: cx - (cx - p.x) * ratio,
          y: cy - (cy - p.y) * ratio
        }));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Convert screen coords to image-space coords. Uses the SVG's
  // getScreenCTM so it Just Works regardless of zoom/pan state.
  const toImageCoords = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg || !svg.getScreenCTM()) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const inv = svg.getScreenCTM().inverse();
    const local = pt.matrixTransform(inv);
    return { x: local.x, y: local.y };
  }, []);

  // ── PAN (hand tool) ──────────────────────────────────────────────────
  // Mouse-button check only applies to actual mouse events. Pen + touch
  // pointers don't set e.button to 0 the same way; we let them through
  // by checking pointerType instead.
  const startPan = (e) => {
    if (tool !== 'hand') return;
    const isMouse = e.pointerType ? e.pointerType === 'mouse' : true;
    if (isMouse && e.button !== undefined && e.button !== 0) return;
    draggingRef.current = true;
    setDragging(true);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const movePan = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPan(p => ({ x: p.x + dx, y: p.y + dy }));
  };
  const endPan = () => {
    draggingRef.current = false;
    setDragging(false);
  };

  // ── POINTER (mouse + pen + touch) ────────────────────────────────────
  // Pointer events unify mouse, stylus, and touch input. We additionally
  // track concurrent pointers in pointersRef so we can detect a two-finger
  // pinch and zoom on touch screens.
  const pointersRef = useRef(new Map());
  const lastPinchDistRef = useRef(null);
  const handlePointerDown = (e) => {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      // Second finger landed — start pinch zoom; abort any in-progress
      // pan or draw so we don't smear a line while zooming.
      const [p1, p2] = [...pointersRef.current.values()];
      lastPinchDistRef.current = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (draggingRef.current) endPan();
      if (draftShape) setDraftShape(null);
      return;
    }
    // Capture so subsequent move/up events keep firing on this element
    // even when the pointer leaves the bounding box (pen + touch only).
    try { e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    if (tool === 'hand') startPan(e);
    else if (tool === 'eraser') eraserClick(e);
    else startDraw(e);
  };
  const handlePointerMove = (e) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pointersRef.current.size === 2) {
      // Pinch — apply same zoom-around-midpoint math as the wheel handler.
      const [p1, p2] = [...pointersRef.current.values()];
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (lastPinchDistRef.current && containerRef.current) {
        const factor = dist / lastPinchDistRef.current;
        const rect = containerRef.current.getBoundingClientRect();
        const cx = (p1.x + p2.x) / 2 - rect.left - rect.width / 2;
        const cy = (p1.y + p2.y) / 2 - rect.top - rect.height / 2;
        setZoom(prev => {
          const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev * factor));
          const ratio = next / prev;
          setPan(p => ({
            x: cx - (cx - p.x) * ratio,
            y: cy - (cy - p.y) * ratio
          }));
          return next;
        });
      }
      lastPinchDistRef.current = dist;
      return;
    }
    if (tool === 'hand') movePan(e);
    else if (draftShape) continueDraw(e);
  };
  const handlePointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) lastPinchDistRef.current = null;
    if (pointersRef.current.size === 0) {
      if (tool === 'hand') endPan();
      else endDraw();
    }
  };

  const resetView = () => {
    if (initialFocus === 'top20' && fitSize.h) {
      focusTopFraction(fitSize.h, 0.2, initialZoom);
      return;
    }
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };
  const zoomIn  = () => setZoom(z => Math.min(MAX_ZOOM, z * 1.25));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, z / 1.25));

  // ── DRAWING ──────────────────────────────────────────────────────────
  const startDraw = (e) => {
    if (tool === 'hand' || tool === 'eraser') return;
    e.preventDefault();
    e.stopPropagation();
    const p = toImageCoords(e);
    if (tool === 'pen' || tool === 'highlighter') {
      setDraftShape({
        type: tool,
        points: [p.x, p.y],
        color,
        strokeWidth: tool === 'highlighter' ? strokeWidth * 4 : strokeWidth,
        opacity: tool === 'highlighter' ? 0.4 : 1
      });
    } else if (tool === 'rect') {
      setDraftShape({ type: 'rect', x: p.x, y: p.y, w: 0, h: 0, color, strokeWidth, opacity: 1 });
    } else if (tool === 'arrow') {
      setDraftShape({ type: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y, color, strokeWidth, opacity: 1 });
    } else if (tool === 'text') {
      // For text we don't draw on drag — we drop a temporary textarea.
      setTextEntry({ x: p.x, y: p.y, value: '' });
    }
  };

  const continueDraw = (e) => {
    if (!draftShape) return;
    const p = toImageCoords(e);
    setDraftShape(prev => {
      if (!prev) return prev;
      if (prev.type === 'pen' || prev.type === 'highlighter') {
        return { ...prev, points: [...prev.points, p.x, p.y] };
      }
      if (prev.type === 'rect') {
        return { ...prev, w: p.x - prev.x, h: p.y - prev.y };
      }
      if (prev.type === 'arrow') {
        return { ...prev, x2: p.x, y2: p.y };
      }
      return prev;
    });
  };

  const endDraw = () => {
    if (!draftShape) return;
    // Discard zero-size shapes (a stray click)
    const trivial =
      (draftShape.type === 'rect'  && Math.abs(draftShape.w) < 2 && Math.abs(draftShape.h) < 2) ||
      (draftShape.type === 'arrow' && Math.hypot(draftShape.x2 - draftShape.x1, draftShape.y2 - draftShape.y1) < 2) ||
      ((draftShape.type === 'pen' || draftShape.type === 'highlighter') && draftShape.points.length < 4);
    if (!trivial) {
      setAnnotations(a => [...a, { ...draftShape, id: Date.now() + Math.random() }]);
    }
    setDraftShape(null);
  };

  // Click-eraser: removes the topmost annotation whose bounding box
  // contains the click. Cheap implementation good enough for screenshots.
  const eraserClick = (e) => {
    if (tool !== 'eraser') return;
    const p = toImageCoords(e);
    setAnnotations(prev => {
      for (let i = prev.length - 1; i >= 0; i--) {
        const s = prev[i];
        if (hits(s, p)) return prev.filter((_, idx) => idx !== i);
      }
      return prev;
    });
  };

  const undo = () => setAnnotations(prev => prev.slice(0, -1));
  const clearAll = () => setAnnotations([]);

  // Commit the in-progress text and add it to annotations.
  const commitText = () => {
    if (textEntry && textEntry.value.trim()) {
      setAnnotations(a => [...a, {
        type: 'text',
        x: textEntry.x,
        y: textEntry.y,
        value: textEntry.value,
        color,
        fontSize: Math.max(12, strokeWidth * 6),
        id: Date.now() + Math.random()
      }]);
    }
    setTextEntry(null);
  };

  // ── SCREENSHOT ───────────────────────────────────────────────────────
  // Composite image + SVG annotations onto a canvas (avoids html2canvas
  // "Unable to find element in cloned iframe" failures on SVG overlays).
  // Output is full image resolution ×2 — pan/zoom are ignored so marks
  // stay registered to the paper. Save + Copy share this path.
  const loadImageForExport = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for export'));
    img.src = src;
  });

  const renderCompositeBlob = useCallback(async () => {
    const imgEl = imageRef.current;
    if (!imgEl || !imgEl.complete) throw new Error('Image not ready');
    const w = imgEl.naturalWidth || imgSize.w;
    const h = imgEl.naturalHeight || imgSize.h;
    if (!w || !h) throw new Error('Image has no size');

    const scale = exportScale > 0 ? exportScale : 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = background || '#0a0a0a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    try {
      ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
    } catch (_) {
      const fresh = await loadImageForExport(imgEl.currentSrc || imgEl.src || url);
      ctx.drawImage(fresh, 0, 0, canvas.width, canvas.height);
    }

    const svg = svgRef.current;
    if (svg) {
      const clone = svg.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', String(w));
      clone.setAttribute('height', String(h));
      if (!clone.getAttribute('viewBox')) {
        clone.setAttribute('viewBox', `0 0 ${w} ${h}`);
      }
      // Uncommitted text lives in an HTML overlay — bake it into the SVG clone.
      if (textEntry && String(textEntry.value || '').trim()) {
        const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        t.setAttribute('x', String(textEntry.x));
        t.setAttribute('y', String(textEntry.y));
        t.setAttribute('fill', color);
        t.setAttribute('font-size', String(Math.max(12, strokeWidth * 6)));
        t.setAttribute('font-family', 'sans-serif');
        t.textContent = textEntry.value;
        clone.appendChild(t);
      }
      const xml = new XMLSerializer().serializeToString(clone);
      const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
      const svgImg = await loadImageForExport(svgUrl);
      ctx.drawImage(svgImg, 0, 0, canvas.width, canvas.height);
    }

    return await new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas produced an empty blob'));
      }, exportMime || 'image/png', exportMime === 'image/jpeg' ? exportQuality : undefined);
    });
  }, [background, imgSize.w, imgSize.h, url, textEntry, color, strokeWidth, exportScale, exportMime, exportQuality]);

  const explainCaptureError = (err) => {
    return /tainted|insecure/i.test(err?.message || '')
      ? 'Cannot capture: the image server blocked screenshot capture (no CORS header). Ask the admin to add an Access-Control-Allow-Origin header on the image origin (e.g. the S3 bucket).'
      : 'Failed to capture screenshot: ' + (err?.message || err);
  };

  // Save: composite → either hand to parent (recheck queue) or download PNG.
  const saveScreenshot = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await renderCompositeBlob();
      if (!blob) return;
      const baseName = (alt || 'screenshot').replace(/\.[^/.]+$/, '');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `${baseName}_annotated_${ts}.png`;
      if (typeof onSaveForRecheck === 'function') {
        await onSaveForRecheck(blob, fileName);
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Screenshot save failed:', err);
      alert(explainCaptureError(err));
    } finally {
      setExporting(false);
    }
  }, [alt, renderCompositeBlob, onSaveForRecheck]);

  // Copy: composite → clipboard as image/png. The user can paste straight
  // into WhatsApp / chat / email / Word without first saving + reopening.
  //
  // Two-path strategy because the modern Clipboard API
  // (navigator.clipboard.write + ClipboardItem) only works in a "secure
  // context" — HTTPS or localhost. The LAN deployment serves over plain
  // HTTP on a private hostname (fast107:3000), so Chrome flags it as
  // insecure and the modern API is undefined.
  //
  // Fallback: render the image inside a hidden contentEditable div,
  // select it, and call document.execCommand('copy'). The deprecated
  // execCommand path still works in insecure contexts in Chrome/Edge
  // and copies a real image (not a data URL string) — paste-able into
  // Word, Outlook, WhatsApp Web, Slack, etc.
  const copyImageViaSelection = (blob) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const holder = document.createElement('div');
      holder.contentEditable = 'true';
      Object.assign(holder.style, {
        position: 'fixed',
        left: '-10000px',
        top: '0',
        opacity: '0',
        pointerEvents: 'none'
      });
      holder.appendChild(img);
      document.body.appendChild(holder);
      try {
        const range = document.createRange();
        range.selectNode(img);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        const ok = document.execCommand('copy');
        sel.removeAllRanges();
        if (ok) resolve();
        else reject(new Error('execCommand("copy") returned false'));
      } catch (err) {
        reject(err);
      } finally {
        try { document.body.removeChild(holder); } catch (_) {}
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for clipboard'));
    };
    img.src = url;
  });

  const copyScreenshot = useCallback(async () => {
    setCopyState('copying');
    try {
      const blob = await renderCompositeBlob();
      if (!blob) { setCopyState('idle'); return; }

      // Prefer the modern API when available (cleaner, works in iframes,
      // produces a proper image/png MIME entry); fall back to the
      // execCommand selection trick on insecure contexts.
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
      } else {
        await copyImageViaSelection(blob);
      }

      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch (err) {
      console.error('Screenshot copy failed:', err);
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2000);
      alert(explainCaptureError(err));
    }
  }, [renderCompositeBlob]);

  // ── RENDER ───────────────────────────────────────────────────────────
  const handleImgLoad = () => {
    const img = imageRef.current;
    if (img) {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
      // Fit after natural size is known (layout may not have settled yet).
      requestAnimationFrame(() => recomputeFit());
    }
  };

  const cursorForTool = (() => {
    if (tool === 'hand') return dragging ? 'grabbing' : 'grab';
    if (tool === 'eraser') return 'cell';
    if (tool === 'text') return 'text';
    return 'crosshair';
  })();

  // Outer flex row: toolbar on the LEFT, image canvas on the RIGHT.
  // The toolbar used to overlay the image as an absolutely-positioned
  // column; pulling it out into its own flex slot keeps the image area
  // unobstructed (no annotations or marks hidden behind the toolbar),
  // and the html2canvas capture — which still runs against
  // containerRef.current (the image area only) — automatically excludes
  // the toolbar without needing the data-html2canvas-ignore hint.
  // The stage wrapper inside containerRef still applies one transform
  // to both image AND svg so annotations stay anchored during zoom/pan.
  return (
    <div
      style={{
        width,
        height,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 8,
        background: 'transparent'
      }}
    >
    {/* Toolbar — single column, always visible beside the image. */}
    <div
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        flex: '0 0 auto',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(15,23,42,0.92)',
        padding: '8px 6px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        overflowY: 'auto',
        touchAction: 'auto',
        width: 48
      }}
    >
      {showTool('hand') && (
        <ToolButton active={tool === 'hand'} onClick={() => setTool('hand')} title="Move / pan (drag)">
          <FontAwesomeIcon icon={faHand} />
        </ToolButton>
      )}
      {showTool('pen') && (
        <ToolButton active={tool === 'pen'} onClick={() => setTool('pen')} title={String(color).toLowerCase() === '#ffffff' ? 'White pen' : 'Pen (free draw)'}>
          <FontAwesomeIcon icon={faPen} />
        </ToolButton>
      )}
      {showTool('highlighter') && (
        <ToolButton active={tool === 'highlighter'} onClick={() => setTool('highlighter')} title="Highlighter">
          <FontAwesomeIcon icon={faHighlighter} />
        </ToolButton>
      )}
      {showTool('rect') && (
        <ToolButton active={tool === 'rect'} onClick={() => setTool('rect')} title="Rectangle">
          <RectIcon />
        </ToolButton>
      )}
      {showTool('arrow') && (
        <ToolButton active={tool === 'arrow'} onClick={() => setTool('arrow')} title="Arrow">
          <FontAwesomeIcon icon={faArrowRight} style={{ transform: 'rotate(-45deg)' }} />
        </ToolButton>
      )}
      {showTool('text') && (
        <ToolButton active={tool === 'text'} onClick={() => setTool('text')} title="Text">
          <FontAwesomeIcon icon={faFont} />
        </ToolButton>
      )}
      {showTool('eraser') && (
        <ToolButton active={tool === 'eraser'} onClick={() => setTool('eraser')} title="Eraser (click a shape)">
          <FontAwesomeIcon icon={faEraser} />
        </ToolButton>
      )}
      <Divider vertical />
      {colorSwatches.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => setColor(c)}
          title={c.toLowerCase() === '#ffffff' ? 'White pen' : (currentUser?.role === 'teacher' ? 'Teacher ink (red)' : 'Examiner ink (green)')}
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: c,
            border: color === c ? '2px solid #93C5FD' : '1px solid rgba(255,255,255,0.45)',
            cursor: customInk ? 'pointer' : 'default',
            padding: 0,
            flexShrink: 0
          }}
        />
      ))}
      {lockedStroke == null && (
        <>
          <Divider vertical />
          <label
            title="Stroke size"
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: 9,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              margin: '2px 0'
            }}
          >
            Size
            <input
              type="range"
              min="1"
              max={String(maxStroke)}
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(parseInt(e.target.value, 10))}
              orient="vertical"
              style={{
                writingMode: 'vertical-lr',
                direction: 'rtl',
                WebkitAppearance: 'slider-vertical',
                appearance: 'slider-vertical',
                width: 18,
                height: 88,
                margin: 0,
                padding: 0,
                cursor: 'pointer'
              }}
            />
            <span style={{ fontSize: 10, fontWeight: 700 }}>{strokeWidth}</span>
          </label>
        </>
      )}
      <Divider vertical />
      <ToolButton onClick={undo} disabled={annotations.length === 0} title="Undo last"><FontAwesomeIcon icon={faRotateLeft} /></ToolButton>
      <ToolButton onClick={clearAll} disabled={annotations.length === 0} title="Clear all annotations"><FontAwesomeIcon icon={faTrash} /></ToolButton>
      <Divider vertical />
      <button onClick={zoomOut} title="Zoom out" style={iconBtnStyle}>
        <FontAwesomeIcon icon={faMinus} />
      </button>
      <span style={{ color: 'white', fontSize: 10, textAlign: 'center', fontWeight: 700, lineHeight: 1.1 }}>
        {Math.round(zoom * 100)}%
      </span>
      <button onClick={zoomIn} title="Zoom in" style={iconBtnStyle}>
        <FontAwesomeIcon icon={faPlus} />
      </button>
      <button onClick={resetView} title="Reset view (or double-click on hand tool)" style={iconBtnStyle}>
        <FontAwesomeIcon icon={faRotate} />
      </button>
      <Divider vertical />
      <button
        onClick={copyScreenshot}
        disabled={copyState === 'copying'}
        title="Copy composite (image + annotations) to the clipboard"
        style={{
          background: copyState === 'copying'
            ? '#6b7280'
            : copyState === 'copied'
              ? 'linear-gradient(135deg, #22c55e, #16a34a)'
              : 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          padding: '6px 0',
          width: '100%',
          cursor: copyState === 'copying' ? 'wait' : 'pointer',
          fontWeight: 700,
          fontSize: 10,
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          boxShadow: '0 2px 6px rgba(59,130,246,0.35)'
        }}
      >
        <FontAwesomeIcon icon={copyState === 'copied' ? faCheck : faCopy} />
        {copyState === 'copying' ? '…' : copyState === 'copied' ? 'OK' : 'Copy'}
      </button>
      <button
        onClick={saveScreenshot}
        disabled={exporting || (requireMarks && annotations.length === 0)}
        title={saveTitle || (onSaveForRecheck
          ? 'Save annotated screenshot into this student\'s re-check request'
          : 'Save screenshot (image + annotations) as PNG')}
        style={{
          background: (exporting || (requireMarks && annotations.length === 0)) ? '#6b7280' : 'linear-gradient(135deg, #10b981, #059669)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          padding: '6px 0',
          width: '100%',
          cursor: (exporting || (requireMarks && annotations.length === 0)) ? 'not-allowed' : 'pointer',
          fontWeight: 700,
          fontSize: 10,
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          boxShadow: '0 2px 6px rgba(16,185,129,0.35)'
        }}
      >
        <FontAwesomeIcon icon={faCamera} />
        {exporting ? '…' : (saveButtonLabel || 'Save')}
      </button>
    </div>

    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={(e) => {
        // Only treat pointerLeave as end-of-gesture for mouse (where we
        // can't setPointerCapture reliably for buttons-not-held cases).
        // For pen/touch the capture keeps the events flowing, so we don't
        // want to abort early.
        if (e.pointerType === 'mouse') handlePointerUp(e);
      }}
      onDoubleClick={() => { if (tool === 'hand') resetView(); }}
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        height: '100%',
        overflow: 'hidden',
        background,
        borderRadius: 8,
        cursor: cursorForTool,
        userSelect: 'none',
        // Stop the browser from interpreting touch gestures as page
        // scroll / pinch-zoom — we handle them ourselves above.
        touchAction: 'none'
      }}
    >
      <div
        ref={stageRef}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
          transition: dragging ? 'none' : 'transform 0.08s ease-out'
        }}
      >
        <div
          style={{
            position: 'relative',
            width: fitSize.w || '100%',
            height: fitSize.h || '100%',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        >
          <img
            ref={imageRef}
            src={url}
            alt={alt}
            draggable={false}
            onLoad={handleImgLoad}
            // No `crossOrigin` attribute — setting it causes the browser to
            // refuse the image when the source server doesn't return CORS
            // headers (typical for S3 buckets without a CORS policy).
            style={{
              display: 'block',
              width: fitSize.w ? `${fitSize.w}px` : '100%',
              height: fitSize.h ? `${fitSize.h}px` : '100%',
              objectFit: 'contain',
              pointerEvents: 'none'
            }}
          />
          {imgSize.w > 0 && (
            <svg
              ref={svgRef}
              viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
              preserveAspectRatio="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: tool === 'hand' ? 'none' : 'auto'
              }}
            >
              {annotations.map(renderShape)}
              {draftShape && renderShape(draftShape, 'draft')}
            </svg>
          )}
        </div>
      </div>

      {/* Text-entry overlay — positioned in screen coords directly above
          the click point. Commit on Enter or blur, cancel on Esc. */}
      {textEntry && (
        <TextEntryOverlay
          key="text-entry"
          stageRef={stageRef}
          svgRef={svgRef}
          x={textEntry.x}
          y={textEntry.y}
          value={textEntry.value}
          color={color}
          fontSize={Math.max(12, strokeWidth * 6)}
          onChange={(v) => setTextEntry(s => ({ ...s, value: v }))}
          onCommit={commitText}
          onCancel={() => setTextEntry(null)}
        />
      )}

      {/* Bottom hint — also excluded from screenshot. */}
      <div
        data-html2canvas-ignore="true"
        style={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.6)',
          fontSize: 11,
          background: 'rgba(0,0,0,0.55)',
          padding: '3px 10px',
          borderRadius: 4,
          pointerEvents: 'none',
          whiteSpace: 'nowrap'
        }}
      >
        {tool === 'hand'
          ? 'Scroll = zoom · Drag = pan · Double-click = reset'
          : tool === 'eraser'
            ? 'Click any shape to erase it'
            : tool === 'text'
              ? 'Click where you want to type · Esc cancels · Enter commits'
              : 'Drag to draw · Switch to ✋ to pan'}
      </div>
    </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Tiny presentational helpers

function ToolButton({ active, disabled, onClick, title, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: active ? 'rgba(59,130,246,0.6)' : 'rgba(255,255,255,0.15)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 6,
        width: 32,
        height: 32,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontSize: 14,
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1
      }}
    >
      {children}
    </button>
  );
}

function Divider({ vertical }) {
  // `vertical` here means "parent flex direction is vertical" — so the
  // divider should be a horizontal line. The original (horizontal toolbar)
  // usage with no prop stays as a vertical line.
  return vertical
    ? <span style={{ height: 1, width: '80%', background: 'rgba(255,255,255,0.2)', margin: 0 }} />
    : <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />;
}

const iconBtnStyle = {
  background: 'rgba(255,255,255,0.15)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: 6,
  width: 32,
  height: 32,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 14,
  padding: 0
};

// Render a single annotation shape as an SVG element.
function renderShape(s, prefix = '') {
  const key = prefix + (s.id != null ? s.id : 'draft');
  const stroke = s.color;
  const strokeWidth = s.strokeWidth || 3;
  const opacity = s.opacity != null ? s.opacity : 1;
  if (s.type === 'pen' || s.type === 'highlighter') {
    const pts = s.points || [];
    let d = '';
    for (let i = 0; i < pts.length; i += 2) {
      d += (i === 0 ? 'M' : 'L') + ' ' + pts[i] + ' ' + pts[i + 1] + ' ';
    }
    return <path key={key} d={d} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" fill="none" opacity={opacity} />;
  }
  if (s.type === 'rect') {
    const x = s.w < 0 ? s.x + s.w : s.x;
    const y = s.h < 0 ? s.y + s.h : s.y;
    const w = Math.abs(s.w);
    const h = Math.abs(s.h);
    return <rect key={key} x={x} y={y} width={w} height={h} stroke={stroke} strokeWidth={strokeWidth} fill="none" opacity={opacity} />;
  }
  if (s.type === 'arrow') {
    const { x1, y1, x2, y2 } = s;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const headLen = Math.max(10, strokeWidth * 3);
    const hx1 = x2 - headLen * Math.cos(angle - Math.PI / 7);
    const hy1 = y2 - headLen * Math.sin(angle - Math.PI / 7);
    const hx2 = x2 - headLen * Math.cos(angle + Math.PI / 7);
    const hy2 = y2 - headLen * Math.sin(angle + Math.PI / 7);
    return (
      <g key={key} opacity={opacity}>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
        <polygon points={`${x2},${y2} ${hx1},${hy1} ${hx2},${hy2}`} fill={stroke} />
      </g>
    );
  }
  if (s.type === 'text') {
    return (
      <text key={key} x={s.x} y={s.y} fill={s.color} fontSize={s.fontSize} fontFamily="sans-serif" opacity={opacity} style={{ whiteSpace: 'pre' }}>
        {s.value}
      </text>
    );
  }
  return null;
}

// Bounding-box hit-test for the eraser tool. Approximate but good enough
// for "click roughly on a shape to remove it".
function hits(s, p) {
  const m = 6; // hit margin in image-px
  if (s.type === 'rect') {
    const x = s.w < 0 ? s.x + s.w : s.x;
    const y = s.h < 0 ? s.y + s.h : s.y;
    const w = Math.abs(s.w);
    const h = Math.abs(s.h);
    return p.x >= x - m && p.x <= x + w + m && p.y >= y - m && p.y <= y + h + m;
  }
  if (s.type === 'arrow') {
    return distToSegment(p, { x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }) <= (s.strokeWidth || 3) + m;
  }
  if (s.type === 'pen' || s.type === 'highlighter') {
    const pts = s.points || [];
    for (let i = 0; i < pts.length - 2; i += 2) {
      const d = distToSegment(p, { x: pts[i], y: pts[i + 1] }, { x: pts[i + 2], y: pts[i + 3] });
      if (d <= (s.strokeWidth || 3) + m) return true;
    }
    return false;
  }
  if (s.type === 'text') {
    const w = (s.value || '').length * (s.fontSize * 0.6);
    return p.x >= s.x - m && p.x <= s.x + w + m && p.y >= s.y - s.fontSize - m && p.y <= s.y + m;
  }
  return false;
}

function distToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

// ── Text-entry overlay ─────────────────────────────────────────────────
// A <textarea> positioned over the click point in SCREEN coords. We
// compute its position by mapping the image-space click back to client
// coords via the SVG's CTM. Esc cancels, Enter commits (Shift+Enter for
// newlines), blur commits.
function TextEntryOverlay({ stageRef, svgRef, x, y, value, color, fontSize, onChange, onCommit, onCancel }) {
  const taRef = useRef(null);
  const [screenPos, setScreenPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !svg.getScreenCTM()) return;
    const ctm = svg.getScreenCTM();
    const pt = svg.createSVGPoint();
    pt.x = x; pt.y = y;
    const screen = pt.matrixTransform(ctm);
    setScreenPos({ left: screen.x, top: screen.y - fontSize });
  }, [x, y, fontSize, svgRef]);

  useEffect(() => {
    const ta = taRef.current;
    if (ta) {
      ta.focus();
      ta.select();
    }
  }, []);

  return (
    <textarea
      ref={taRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(); }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      placeholder="Type then Enter…"
      style={{
        position: 'fixed',
        left: screenPos.left,
        top: screenPos.top,
        background: 'rgba(255,255,255,0.95)',
        color,
        fontSize,
        fontFamily: 'sans-serif',
        padding: '2px 4px',
        border: '1px dashed ' + color,
        borderRadius: 3,
        outline: 'none',
        minWidth: 120,
        minHeight: 24,
        resize: 'both',
        zIndex: 20
      }}
    />
  );
}
